import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import Reveal from '../components/Reveal';
import BrandLogo from '../components/BrandLogo';
import { useBestsellers } from '../hooks/useBestsellers';
import { useProducts } from '../hooks/useProducts';
import {
  cropMediaStyle,
  fetchCollectionHeaders,
  getCollectionHeader,
  SPORT_HOME_LAYOUT,
} from '../lib/collectionHeaders';
import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';
import { cssUrl, getProductImageUrl } from '../utils/imageUrl';
import { productBelongsToSport, SPORTS } from '../utils/sports';
import './Home.css';

const INTERVAL_MS = 5000;
const DEFAULT_TITLE = 'The collection.';
const DEFAULT_SUBTITLE =
  'Officially licensed kits, edited into a quieter wardrobe.';

const SPORT_TONES = {
  'formula-series': '#1c1c1c',
  football: '#1a2420',
  basketball: '#241c18',
  baseball: '#1a1f24',
  volleyball: '#222018',
  cricket: '#18241c',
  rugby: '#241818',
};

function SportTile({ sport, cover, layout }) {
  return (
    <Link
      to={`/sports/${sport.slug}`}
      className={`home-sport-tile home-sport-tile--${layout}`}
      style={{ backgroundColor: SPORT_TONES[sport.slug] || '#1a1a1a' }}
    >
      {cover?.imageUrl ? (
        <span
          className="home-sport-tile-media"
          style={{
            backgroundImage: cssUrl(cover.imageUrl),
            ...cropMediaStyle(cover.crop),
          }}
          aria-hidden="true"
        />
      ) : null}
      <span className="home-sport-tile-shade" aria-hidden="true" />
      <span className="home-sport-tile-copy">
        <span className="home-sport-tile-name">{sport.name}</span>
        <span className="home-sport-tile-action">Shop</span>
      </span>
    </Link>
  );
}

function Home() {
  const [heroImages, setHeroImages] = useState([]);
  const [heroTitle, setHeroTitle] = useState(DEFAULT_TITLE);
  const [heroSubtitle, setHeroSubtitle] = useState(DEFAULT_SUBTITLE);
  const [collectionHeaders, setCollectionHeaders] = useState({});
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const { bestsellers, loading } = useBestsellers();
  const { products } = useProducts();

  useEffect(() => {
    if (!isSupabaseConfigured) return undefined;

    let cancelled = false;

    async function loadHero() {
      const settingsResult = await supabase
        .from('homepage_settings')
        .select('hero_image_url, hero_images, hero_title, hero_subtitle')
        .eq('id', 1)
        .maybeSingle();

      if (cancelled || !settingsResult.data) return;

      const data = settingsResult.data;
      let slides = Array.isArray(data.hero_images) ? data.hero_images : [];
      if (!slides.length && data.hero_image_url) {
        slides = [{ image_url: data.hero_image_url, sort_order: 0 }];
      }

      setHeroImages(
        [...slides]
          .filter(slide => slide?.image_url)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      );

      if (data.hero_title) setHeroTitle(data.hero_title);
      if (data.hero_subtitle) setHeroSubtitle(data.hero_subtitle);
    }

    loadHero();
    fetchCollectionHeaders()
      .then(headers => {
        if (!cancelled) setCollectionHeaders(headers);
      })
      .catch(() => {
        if (!cancelled) setCollectionHeaders({});
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setIndex(0);
  }, [heroImages.length]);

  useEffect(() => {
    if (heroImages.length <= 1 || paused) return undefined;

    const timer = setInterval(() => {
      setIndex(current => (current + 1) % heroImages.length);
    }, INTERVAL_MS);

    return () => clearInterval(timer);
  }, [heroImages.length, paused, index]);

  const active = heroImages[index] || null;
  const title = active?.title || heroTitle;
  const subtitle = active?.subtitle || heroSubtitle;

  const sportCovers = useMemo(() => {
    const map = {};
    SPORTS.forEach(sport => {
      const uploaded = getCollectionHeader(collectionHeaders, sport.slug);
      if (uploaded) {
        map[sport.slug] = {
          imageUrl: uploaded.image_url,
          crop: uploaded.home_crop,
        };
        return;
      }
      const match = (products || []).find(product =>
        productBelongsToSport(product, sport)
      );
      const fallback = match ? getProductImageUrl(match) : '';
      if (fallback) map[sport.slug] = { imageUrl: fallback };
    });
    return map;
  }, [products, collectionHeaders]);

  const editorialProduct = bestsellers[0] || null;
  const editorialImage =
    (active && active.image_url) ||
    (editorialProduct && getProductImageUrl(editorialProduct)) ||
    null;

  const spotlightSport = SPORTS[1] || SPORTS[0];
  const spotlightProducts = useMemo(() => {
    if (!spotlightSport) return [];
    return (bestsellers.length ? bestsellers : products || [])
      .filter(product => productBelongsToSport(product, spotlightSport))
      .slice(0, 3);
  }, [bestsellers, products, spotlightSport]);

  return (
    <div className="home">
      <section
        className="home-hero"
        data-topbar-surface="dark"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        aria-label="Homepage banner"
      >
        <div className="home-hero-media">
          {heroImages.length === 0 ? (
            <div className="home-hero-media-empty">
              <p className="home-hero-wordmark" aria-hidden="true">
                Laurenti — Laurenti — Laurenti — Laurenti — Laurenti —
              </p>
            </div>
          ) : (
            heroImages.map((slide, slideIndex) => (
              <div
                key={slide.image_url + slideIndex}
                className={`home-hero-slide${slideIndex === index ? ' active' : ''}`}
                style={{ backgroundImage: cssUrl(slide.image_url) }}
                aria-hidden={slideIndex !== index}
              />
            ))
          )}
          <div className="home-hero-shade" aria-hidden="true" />
        </div>

        <div className="home-hero-copy">
          <p className="home-hero-brand">
            <BrandLogo />
          </p>
          <h1 key={`title-${index}`}>{title}</h1>
          <p key={`lede-${index}`} className="home-hero-lede">
            {subtitle}
          </p>
          <div className="home-hero-actions">
            <Link to="/shop" className="store-btn store-btn--inverse">
              Enter the collection
            </Link>
            <Link to={`/sports/${SPORTS[0]?.slug || 'formula-series'}`} className="home-hero-link">
              Browse sports
            </Link>
          </div>
        </div>

        {heroImages.length > 1 && (
          <div className="home-hero-progress" role="tablist" aria-label="Hero slides">
            {heroImages.map((slide, slideIndex) => (
              <button
                key={slide.image_url + slideIndex}
                type="button"
                role="tab"
                aria-selected={slideIndex === index}
                aria-label={`Slide ${slideIndex + 1}`}
                className={slideIndex === index ? 'active' : ''}
                onClick={() => setIndex(slideIndex)}
              />
            ))}
          </div>
        )}

        <a href="#sports-index" className="home-hero-scroll">
          Scroll
        </a>
      </section>

      <div className="home-ticker" aria-hidden="true">
        <div className="home-ticker-track">
          {[0, 1].map(copy => (
            <p key={copy} className="home-ticker-copy">
              {SPORTS.map(sport => sport.name).join('  ·  ')}
              {'  ·  '}Licensed goods{'  ·  '}Secure checkout{'  ·  '}
            </p>
          ))}
        </div>
      </div>

      <Reveal as="section" className="home-sports" aria-label="Sports" id="sports-index">
        <div className="home-section-inner">
          <p className="store-eyebrow">Enter by sport</p>
          <h2 className="home-section-title">The index</h2>
          <div className="home-sport-bento">
            <div className="home-sport-bento-feature">
              {SPORTS.filter(
                sport => SPORT_HOME_LAYOUT[sport.slug] !== 'compact'
              ).map(sport => (
                <SportTile
                  key={sport.slug}
                  sport={sport}
                  cover={sportCovers[sport.slug]}
                  layout={SPORT_HOME_LAYOUT[sport.slug]}
                />
              ))}
            </div>
            <div className="home-sport-bento-row">
              {SPORTS.filter(
                sport => SPORT_HOME_LAYOUT[sport.slug] === 'compact'
              ).map(sport => (
                <SportTile
                  key={sport.slug}
                  sport={sport}
                  cover={sportCovers[sport.slug]}
                  layout="compact"
                />
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal as="section" className="home-selected" aria-label="Now in the house">
        <div className="home-section-inner">
          <p className="store-eyebrow">Now in the house</p>
          <h2 className="home-section-title">Selected</h2>
          <p className="home-section-lede">
            Pieces currently defining the collection.
          </p>

          {loading && (
            <div className="store-skeleton-grid" aria-hidden="true">
              {[0, 1, 2, 3].map(key => (
                <div key={key} className="store-skeleton" />
              ))}
            </div>
          )}
          {!loading && bestsellers.length === 0 && (
            <p className="home-status">Nothing selected yet. The next drop is coming.</p>
          )}
          {!loading && bestsellers.length > 0 && (
            <div className="product-card-grid">
              {bestsellers.map(product => (
                <ProductCard key={product.id || product.name} product={product} />
              ))}
            </div>
          )}
          <div className="home-section-cta">
            <Link to="/shop" className="store-link">
              View all
            </Link>
          </div>
        </div>
      </Reveal>

      <Reveal as="section" className="home-editorial" aria-label="Editorial">
        <div className="home-editorial-inner">
          <div className="home-editorial-copy">
            <p className="store-eyebrow">The cut</p>
            <h2>Licensed kit. Laurenti cut.</h2>
            <p>
              Authentic club and team apparel, edited into a quieter wardrobe —
              fewer drops, sharper standards, no noise.
            </p>
            <Link to="/shop" className="store-link">
              Browse the house
            </Link>
          </div>
          <div
            className="home-editorial-frame"
            role="img"
            aria-label={editorialProduct?.name || 'Laurenti Apparel collection'}
          >
            {editorialImage ? (
              <span
                className="home-editorial-media"
                style={{ backgroundImage: cssUrl(editorialImage) }}
                aria-hidden="true"
              />
            ) : null}
          </div>
        </div>
      </Reveal>

      {spotlightSport && (
        <Reveal as="section" className="home-spotlight" aria-label="Sport spotlight">
          <div className="home-section-inner">
            <p className="store-eyebrow">Spotlight</p>
            <div className="home-spotlight-head">
              <h2 className="home-section-title">{spotlightSport.name}</h2>
              <Link to={`/sports/${spotlightSport.slug}`} className="store-link">
                Enter {spotlightSport.name}
              </Link>
            </div>
            {spotlightProducts.length > 0 ? (
              <div className="product-card-grid">
                {spotlightProducts.map(product => (
                  <ProductCard key={product.id || product.name} product={product} />
                ))}
              </div>
            ) : (
              <p className="home-status">
                Kits for {spotlightSport.name} land here as the catalogue grows.
              </p>
            )}
          </div>
        </Reveal>
      )}

      <section className="home-trust" aria-label="Trust">
        <div className="home-trust-inner">
          <p>Licensed goods</p>
          <p>Secure checkout</p>
          <p>
            <Link to="/shipping">Shipping policy</Link>
          </p>
        </div>
      </section>
    </div>
  );
}

export default Home;
